import React, { useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  AppDatePicker,
  AppFileUploader,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
  AppDeleteModal,
} from '@/layout/AppComponents';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import AppInput from '../../../../../components/form/AppInput';
import colors from '../../../../../../constants/colors';
import minions from '../../../../../../minions/minions';
import Translator from '../../../../../components/helpers/Translator';

const PlantEvidenceEditForm = (props) => {
  const {
    evidenceFolders,
    selectedEvidenceFolderId,
    selectedEvidence,
    handleEditEvidence,
    onCancel,
    onRemove,
    isLoading,
  } = props;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const validationSchema = Yup.object().shape({
    name: Yup.string().required(
      ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)
    ),
    date: Yup.string().required(
      ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)
    ),
  });

  const toggleRemoveFile = (values, setFieldValue, fileName) => {
    const updateRemoveFiles = values.removeFiles;

    if (updateRemoveFiles.includes(fileName)) {
      setFieldValue(
        'removeFiles',
        updateRemoveFiles.filter((fn) => fn !== fileName)
      );
    } else {
      updateRemoveFiles.push(fileName);
      setFieldValue('removeFiles', updateRemoveFiles);
    }
  };

  const handleUpload = (values, setFieldValue, opt) => {
    const files = Array.from(opt).filter(
      (f) =>
        !values.files.includes(f.name) &&
        selectedEvidence.urls &&
        !selectedEvidence.urls.includes(f.name)
    );
    setFieldValue('files', [...values.files, ...files]);
  };

  const handleCancel = () => {
    if (isLoading) return;
    onCancel();
  };

  const handleOnSubmit = (values) => {
    if (isLoading) return;
    handleEditEvidence(values);
  };

  return (
    <EvidenceForm>
      <Formik
        initialValues={{
          name: selectedEvidence.name,
          description: selectedEvidence.description || '',
          removeFiles: [],
          files: [],
          date: selectedEvidence.date,
          folderId: selectedEvidenceFolderId,
        }}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={handleOnSubmit}
      >
        {({ handleSubmit, values, setFieldValue, errors }) => (
          <div className="p-2">
            <h3>
              <Translator
                file="module-legalCompliance"
                name="module.lc.plant-evidence.edit"
              />
            </h3>
            <AppInput
              title={<Translator file="module-legalCompliance" name="name" />}
              placeholder={
                <Translator
                  file="module-legalCompliance"
                  name="name.description.compulsory"
                />
              }
              icon={<AppRemixIcon icon="double-quotes-l" noVariants />}
              value={values.name}
              onChange={(opt) => setFieldValue('name', opt)}
              error={errors.name}
              isClearable
            />
            <AppSingleSelect
              title={
                <Translator
                  file="module-legalCompliance"
                  name="module.lc.plant-evidence.folder"
                />
              }
              icon={<AppRemixIcon icon="folder" />}
              className="form-control"
              value={values.folderId}
              options={evidenceFolders.map((ef) => ({
                value: ef.id,
                label: ef.name,
              }))}
              onChange={(opt) =>
                setFieldValue('folderId', opt || values.folderId)
              }
            />
            <AppTextArea
              title={
                <Translator
                  file="module-legalCompliance"
                  name="module.lc.evidence.description"
                />
              }
              placeholder={
                <Translator
                  file="module-legalCompliance"
                  name="name.description.compulsory"
                />
              }
              icon={<AppRemixIcon icon="align-left" noVariants />}
              onChange={(opt) => setFieldValue('description', opt)}
              value={values.description}
            />
            <div className="row">
              <div className="col-lg-6">
                <AppDatePicker
                  title={
                    <Translator file="module-legalCompliance" name="date" />
                  }
                  value={values.date}
                  onChange={(opt) => setFieldValue('date', opt)}
                  popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                />
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-lg-12">
                    <EvidenceFileList urls>
                      {Array.isArray(selectedEvidence?.urls) &&
                        selectedEvidence.urls.map((fileName) => (
                          <li
                            key={fileName}
                            title={
                              values.removeFiles.includes(fileName)
                                ? ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-legalCompliance"
                                      name="click.restore"
                                    />
                                  )
                                : ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-legalCompliance"
                                      name="click.delete"
                                    />
                                  )
                            }
                            onClick={() =>
                              toggleRemoveFile(values, setFieldValue, fileName)
                            }
                          >
                            <AppRemixIcon
                              className="pointer mr-3"
                              icon="delete-bin"
                              color={
                                values.removeFiles.includes(fileName)
                                  ? colors.grey400
                                  : colors.danger
                              }
                            />
                            {minions.files.fileIcon(
                              fileName,
                              true,
                              values.removeFiles.includes(fileName)
                                ? colors.grey400
                                : null,
                              'mr-2'
                            )}
                            <FileName
                              isRemoved={values.removeFiles.includes(fileName)}
                            >
                              {fileName}
                            </FileName>
                          </li>
                        ))}
                    </EvidenceFileList>
                  </div>
                  {values.files && (
                    <>
                      <hr />
                      <div className="col-lg-12">
                        <EvidenceFileList urls={false}>
                          {values.files.map((f) => (
                            <li
                              key={f.name}
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-legalCompliance"
                                  name="click.delete"
                                />
                              )}
                              onClick={() =>
                                setFieldValue(
                                  'files',
                                  values.files.filter(
                                    (file) => file.name !== f.name
                                  )
                                )
                              }
                            >
                              <AppRemixIcon
                                className="pointer mr-3"
                                icon="delete-bin"
                                color={colors.danger}
                              />
                              {minions.files.fileIcon(
                                f.name,
                                true,
                                null,
                                'mr-2'
                              )}
                              {f.name}
                            </li>
                          ))}
                        </EvidenceFileList>
                      </div>
                    </>
                  )}
                </div>
                <div className="pt-2">
                  <AppFileUploader
                    isMulti
                    placeholder={
                      values.files.length === 0
                        ? ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="select.one.multiple.files"
                            />
                          )
                        : ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="select.number.of.elements"
                              variables={{ elements: values.files.length }}
                            />
                          )
                    }
                    onChange={(opt) => handleUpload(values, setFieldValue, opt)}
                    btnText={<Translator file="general" name="search..." />}
                  />
                </div>
              </div>
            </div>
            <span
              className="btn btn-outline-danger mt-2"
              onClick={() => setShowDeleteModal(true)}
            >
              <Translator file="general" name="general.delete" />
            </span>
            <div className="float-right mt-2">
              <span
                className={`btn btn-outline-light mr-2 ${
                  isLoading ? 'disabled' : ''
                }`}
                onClick={handleCancel}
              >
                <Translator file="user" name="user.cancel" />
              </span>
              <span
                className={`btn btn-outline-info ${
                  isLoading ? 'disabled' : ''
                }`}
                onClick={handleSubmit}
              >
                <Translator file="general" name="general.update" />
              </span>
            </div>
          </div>
        )}
      </Formik>
      <AppDeleteModal
        show={showDeleteModal}
        title={
          <Translator
            file="module-legalCompliance"
            name="module.lc.remove-plant-evidence-confirm-button"
          />
        }
        onConfirm={() => {
          onRemove();
          setShowDeleteModal(false);
        }}
        onClose={() => setShowDeleteModal(false)}
      />
    </EvidenceForm>
  );
};
export default PlantEvidenceEditForm;

const EvidenceForm = styled.div`
  h3 {
    font-size: 13px;
    font-weight: bold;
  }
`;
const EvidenceFileList = styled.ul`
  list-style: none;
  margin: 10px 0 0 0;
  padding-left: 6px;
  background-color: ${(props) => (props.urls ? colors.grey200 : 'initial')};
  li {
    cursor: pointer;
    display: flex;
    align-items: center;
    border-top: 1px solid ${colors.grey300};
    padding: 8px 0;
    span {
      font-size: 12px;
      color: ${colors.primary};
    }
  }
  li:first-child {
    border-top: 0;
  }
`;
const FileName = styled.span`
  text-decoration: ${(props) => (props.isRemoved ? 'line-through' : 'unset')};
`;
